import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { groupsAPI } from '../services/api';
import '../styles/Groups.css';

const Groups = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [formData, setFormData] = useState({ name: '', color: '#3498db' });

    useEffect(() => {
        loadGroups();
    }, []);

    const loadGroups = async () => {
        try {
            const response = await groupsAPI.getAll();
            setGroups(response.data.groups);
            setLoading(false);
        } catch (error) {
            console.error('Error loading groups:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingGroup) {
                await groupsAPI.update(editingGroup.id, formData);
            } else {
                await groupsAPI.create(formData);
            }

            setShowModal(false);
            setEditingGroup(null);
            setFormData({ name: '', color: '#3498db' });
            loadGroups();
        } catch (error) {
            console.error('Error saving group:', error);
            alert(error.response?.data?.error || 'Failed to save group');
        }
    };

    const handleEdit = (group) => {
        setEditingGroup(group);
        setFormData({ name: group.name, color: group.color });
        setShowModal(true);
    };

    const handleDelete = async (groupId) => {
        if (!window.confirm('Are you sure you want to delete this group? Tasks in this group will not be deleted.')) return;
        
        try {
            await groupsAPI.delete(groupId);
            loadGroups();
        } catch (error) {
            console.error('Error deleting group:', error);
        }
    };

    const openNewGroupModal = () => {
        setEditingGroup(null);
        setFormData({ name: '', color: '#3498db' });
        setShowModal(true);
    };

    const colorPresets = [
        '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
        '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
    ];

    if (loading) {
        return (
            <>
                <Sidebar />
                <div className="main-content">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <div className="main-content">
                <div className="groups-container">
                <div className="groups-header">
                    <h1>📁 Task Groups</h1>
                    <button onClick={openNewGroupModal} className="btn-primary">
                        + Create Group
                    </button>
                </div>

                <div className="groups-grid">
                    {groups.length === 0 ? (
                        <div className="empty-state">
                            <h2>No groups yet</h2>
                            <p>Create your first group to organize your tasks!</p>
                        </div>
                    ) : (
                        groups.map((group) => (
                            <div key={group.id} className="group-card">
                                <div 
                                    className="group-color-bar" 
                                    style={{ backgroundColor: group.color }}
                                ></div>
                                <div className="group-content">
                                    <h3>{group.name}</h3>
                                    <div className="group-color-display">
                                        <div 
                                            className="color-circle" 
                                            style={{ backgroundColor: group.color }}
                                        ></div>
                                        <span>{group.color}</span>
                                    </div>
                                    <div className="group-actions">
                                        <button onClick={() => handleEdit(group)} className="btn-secondary">
                                            ✏️ Edit
                                        </button>
                                        <button onClick={() => handleDelete(group.id)} className="btn-danger">
                                            🗑️ Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{editingGroup ? 'Edit Group' : 'New Group'}</h2>
                                <button onClick={() => setShowModal(false)} className="btn-close">×</button>
                            </div>

                            <form onSubmit={handleSubmit} className="group-form">
                                <div className="form-group">
                                    <label>Group Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter group name (e.g., Work, Personal, Urgent)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Color *</label>
                                    <div className="color-picker-container">
                                        <div className="color-presets">
                                            {colorPresets.map((color) => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    className={`color-preset ${formData.color === color ? 'selected' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => setFormData({ ...formData, color })}
                                                    title={color}
                                                />
                                            ))}
                                        </div>
                                        <div className="custom-color">
                                            <label>Custom Color:</label>
                                            <input
                                                type="color"
                                                name="color"
                                                value={formData.color}
                                                onChange={handleInputChange}
                                            />
                                            <span>{formData.color}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Preview</label>
                                    <div className="group-preview">
                                        <div 
                                            className="preview-badge" 
                                            style={{ backgroundColor: formData.color }}
                                        >
                                            {formData.name || 'Group Name'}
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        {editingGroup ? 'Update Group' : 'Create Group'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default Groups;
